import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mic, MicOff, Volume2, VolumeX, Play, Square, ChevronRight, 
  Award, Target, CheckCircle, Loader, ArrowLeft, Sparkles,
  Star, TrendingUp, Zap, Trophy, BarChart3, RefreshCw,
  Headphones, MessageSquare, Clock, ChevronDown, X, Home, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const speechSynthesis = window.speechSynthesis;

const AiVoiceInterview = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [stage, setStage] = useState('welcome');
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('genel');
  const [questionCount, setQuestionCount] = useState(5);
  const [language, setLanguage] = useState('tr');
  
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [categoryName, setCategoryName] = useState('');
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [results, setResults] = useState(null);
  const [allAnalyses, setAllAnalyses] = useState([]);
  
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const [pulseAnimation, setPulseAnimation] = useState(false);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [savedRecordings, setSavedRecordings] = useState([]);
  const [showSavedRecordings, setShowSavedRecordings] = useState(true);
  
  useEffect(() => {
    fetchCategories();
    loadSavedRecordings();
    
    // Pre-load voices for better Turkish voice selection
    const loadVoices = () => speechSynthesis?.getVoices();
    loadVoices();
    if (speechSynthesis) {
      speechSynthesis.addEventListener("voiceschanged", loadVoices);
    }
    return () => {
      if (speechSynthesis) {
        speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      }
    };
  }, []);

  const deleteRecording = async (recordingId) => {
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/api/voice-recordings/${recordingId}`, { withCredentials: true });
      toast.success("Kayıt silindi");
      loadSavedRecordings();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Kayıt silinemedi");
    }
  };

  const loadSavedRecordings = async () => {
    try {
      const response = await axios.get(`${API}/api/voice-recordings/my-recordings`, { withCredentials: true });
      if (response.data && response.data.recordings) {
        // Filter for AI voice interview recordings
        const aiRecs = response.data.recordings.filter(r => 
          r.category === "ai-voice-interview" || r.exercise_id?.includes("ai-voice")
        );
        setSavedRecordings(aiRecs.length > 0 ? aiRecs : response.data.recordings.slice(0, 10));
      }
    } catch (error) {
      console.error("Error loading recordings:", error);
    }
  };
  
  useEffect(() => {
    if (isRecording) {
      setPulseAnimation(true);
    } else {
      setPulseAnimation(false);
    }
  }, [isRecording]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-interview/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };
  
  const speakQuestion = async (text) => {
    if (!text) return;
    setIsSpeaking(true);
    
    try {
      // Use OpenAI TTS API for high-quality Turkish voice
      const response = await axios.post(
        `${API}/api/tts/generate`,
        { 
          text: text,
          voice: "nova",  // Best voice for Turkish
          speed: 0.9
        },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
        await audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch (error) {
      console.error("TTS Error:", error);
      // Fallback to browser TTS
      if (speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language === "tr" ? "tr-TR" : "en-US";
        utterance.rate = 0.85;
        utterance.onend = () => setIsSpeaking(false);
        speechSynthesis.speak(utterance);
      } else {
        setIsSpeaking(false);
      }
    }
  };


  
  const startRecording = async () => {
    // Clear previous data
    setTranscript("");
    setRecordedAudioBlob(null);
    audioChunksRef.current = [];
    
    // Start audio recording with MediaRecorder
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      toast.success("Kayıt başladı! Konuşun...");
    } catch (err) {
      console.error("Mikrofon hatası:", err);
      toast.error("Mikrofon erişimi sağlanamadı. Lütfen izin verin.");
    }
  };

  const stopRecording = async () => {
    // Stop browser speech recognition (if any)
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    // Stop MediaRecorder and get audio blob
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
    
    // Wait a bit for blob to be created, then transcribe with Whisper
    setTimeout(async () => {
      if (audioChunksRef.current && audioChunksRef.current.length > 0) {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setRecordedAudioBlob(audioBlob);
        
        // Convert to base64 and send to Whisper
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
          try {
            toast.info("Ses yazıya çevriliyor...");
            const response = await axios.post(
              `${API}/api/stt/transcribe`,
              {
                audio_base64: reader.result,
                language: language === "tr" ? "tr" : "en"
              },
              { withCredentials: true }
            );
            
            if (response.data.success && response.data.text) {
              setTranscript(response.data.text);
              toast.success("Ses başarıyla yazıya çevrildi!");
            } else {
              toast.error("Ses yazıya çevrilemedi");
            }
          } catch (err) {
            console.error("Whisper STT error:", err);
            toast.error("Ses tanıma hatası. Lütfen tekrar deneyin.");
          }
        };
      }
    }, 500);
  };

  // Save recording to server
  const saveRecordingToServer = async () => {
    if (!recordedAudioBlob) {
      toast.error("Kaydedilecek ses yok");
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(recordedAudioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        try {
          const response = await axios.post(
            `${API}/api/voice-recordings/save`,
            {
              exercise_id: `ai-voice-q${questionNumber}`,
              exercise_title: currentQuestion?.substring(0, 100) || "AI Mülakat Sorusu",
              category: "ai-voice-interview",
              text: transcript,
              audio_data: base64Audio,
              duration_seconds: 0
            },
            { withCredentials: true }
          );
          
          if (response.data.success) {
            toast.success("Ses kaydı başarıyla kaydedildi!");
            loadSavedRecordings();
          }
        } catch (err) {
          console.error("Save error:", err);
          toast.error("Kayıt kaydedilemedi");
        }
      };
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Kayıt işlemi başarısız");
    }
  };
  
  const startInterview = async () => {
    try {
      const response = await axios.post(`${API}/api/ai-interview/start`, {
        category: selectedCategory,
        language: language,
        question_count: questionCount
      });
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.question);
      setQuestionNumber(response.data.question_number);
      setTotalQuestions(response.data.total_questions);
      setCategoryName(response.data.category);
      setStage('interview');
      setAllAnalyses([]);
      
      setTimeout(() => speakQuestion(response.data.question), 800);
    } catch (error) {
      toast.error('Mülakat başlatılamadı');
    }
  };
  
  const analyzeAnswer = async () => {
    if (!transcript.trim()) {
      toast.warning('Lütfen önce cevabınızı söyleyin');
      return;
    }
    
    setIsAnalyzing(true);
    setShowAnalysis(false);
    
    try {
      const response = await axios.post(`${API}/api/ai-interview/analyze`, {
        session_id: sessionId,
        question: currentQuestion,
        answer: transcript.trim(),
        language: language
      });
      
      setAnalysis(response.data);
      setAllAnalyses(prev => [...prev, { question: currentQuestion, ...response.data }]);
      setShowAnalysis(true);
    } catch (error) {
      toast.error('Analiz sırasında bir hata oluştu');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const nextQuestion = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-interview/next/${sessionId}`);
      
      if (response.data.completed) {
        setResults(response.data);
        setStage('result');
      } else {
        setCurrentQuestion(response.data.question);
        setQuestionNumber(response.data.question_number);
        setTranscript('');
        setAnalysis(null);
        setShowAnalysis(false);
        setTimeout(() => speakQuestion(response.data.question), 500);
      }
    } catch (error) {
      toast.error('Sonraki soru alınamadı');
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };
  
  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-emerald-500 to-teal-500';
    if (score >= 60) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };

  // Welcome Screen
  if (stage === 'welcome') {
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-600/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Ana Menü</span>
          </button>
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-purple-600 to-blue-600 mb-6 shadow-2xl shadow-purple-500/30">
              <Mic size={48} className="text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
              AI Sesli Mülakat
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              DeepSeek yapay zeka ile gerçekçi mülakat deneyimi yaşayın
            </p>
          </div>
          
          {/* Features */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: Headphones, label: 'Sesli Sorular', color: 'text-purple-400' },
              { icon: Mic, label: 'Sesli Cevap', color: 'text-blue-400' },
              { icon: Sparkles, label: 'AI Analiz', color: 'text-cyan-400' },
              { icon: BarChart3, label: 'Detaylı Rapor', color: 'text-emerald-400' },
            ].map((feature, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 text-center">
                <feature.icon className={`mx-auto mb-2 ${feature.color}`} size={28} />
                <span className="text-sm text-gray-300">{feature.label}</span>
              </div>
            ))}
          </div>
          
          {/* Category Selection */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 mb-6 border border-white/10">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="text-purple-400" size={20} />
              Kategori Seçin
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(categories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`p-4 rounded-2xl text-left transition-all duration-300 ${
                    selectedCategory === key 
                      ? 'bg-gradient-to-r from-purple-600/50 to-blue-600/50 border-purple-400/50 shadow-lg shadow-purple-500/20' 
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } border`}
                >
                  <div className="font-medium">{cat.name}</div>
                  <div className="text-sm text-gray-400">{cat.question_count} soru</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Settings */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 mb-8 border border-white/10">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-3">Dil</label>
                <div className="flex gap-2">
                  {[{ code: 'tr', flag: '🇹🇷', label: 'Türkçe' }, { code: 'en', flag: '🇬🇧', label: 'English' }].map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`flex-1 py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                        language === lang.code 
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-3">Soru Sayısı</label>
                <div className="flex gap-2">
                  {[3, 5, 10].map(count => (
                    <button
                      key={count}
                      onClick={() => setQuestionCount(count)}
                      className={`flex-1 py-3 rounded-xl transition-all duration-300 ${
                        questionCount === count 
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Previous Recordings */}
          {savedRecordings.length > 0 && (
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 mb-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Volume2 className="text-green-400" size={20} />
                  Önceki Kayıtlarım ({savedRecordings.length})
                </h2>
                <button 
                  onClick={() => setShowSavedRecordings(!showSavedRecordings)}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  {showSavedRecordings ? "Gizle" : "Göster"}
                </button>
              </div>
              {showSavedRecordings && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {savedRecordings.map((rec, idx) => (
                    <div key={rec.id || idx} className="bg-black/30 rounded-xl p-3 flex items-center gap-3 border border-white/5">
                      <button
                        onClick={() => { const audio = new Audio(rec.audio_data || rec.audio_url); audio.play(); }}
                        className="w-10 h-10 bg-green-500/20 hover:bg-green-500/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <Play size={18} className="text-green-400" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{rec.question_text || rec.exercise_title || "Mülakat Kaydı"}</p>
                        <p className="text-xs text-gray-500">{new Date(rec.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                      <button
                        onClick={() => deleteRecording(rec.id)}
                        className="w-8 h-8 bg-red-500/20 hover:bg-red-500/40 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Start Button */}
          <button
            onClick={startInterview}
            className="w-full py-5 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 rounded-2xl font-bold text-xl flex items-center justify-center gap-3 transition-all duration-300 transform hover:scale-[1.02] shadow-2xl shadow-purple-500/30"
          >
            <Play size={28} />
            Mülakata Başla
          </button>
        </div>
      </div>
    );
  }
  
  // Interview Screen
  if (stage === 'interview') {
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute top-1/3 left-1/3 w-72 h-72 bg-purple-600/20 rounded-full blur-3xl transition-all duration-1000 ${isRecording ? 'scale-150 opacity-50' : 'scale-100 opacity-30'}`}></div>
          <div className={`absolute bottom-1/3 right-1/3 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl transition-all duration-1000 ${isRecording ? 'scale-150 opacity-50' : 'scale-100 opacity-30'}`}></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-6 max-w-3xl">
          {/* Progress */}
          {/* Çıkış Butonları */}
          <div className="flex justify-between items-center mb-6">
            <button onClick={() => setStage('selection')} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-gray-300 hover:text-white transition-all">
              <ArrowLeft size={18} />
              <span>Geri</span>
            </button>
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-xl text-red-400 hover:text-red-300 transition-all">
              <Home size={18} />
              <span>Ana Sayfa</span>
            </button>
          </div>
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-purple-400 font-medium">{categoryName}</span>
              <span className="text-sm text-gray-400">
                <span className="text-white font-bold">{questionNumber}</span> / {totalQuestions}
              </span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 transition-all duration-700"
                style={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Question Card */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 mb-8 border border-white/20 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                  <MessageSquare size={20} />
                </div>
                <span className="text-purple-400 font-medium">Soru {questionNumber}</span>
              </div>
              <button
                onClick={() => speakQuestion(currentQuestion)}
                disabled={isSpeaking}
                className={`p-3 rounded-xl transition-all ${
                  isSpeaking 
                    ? 'bg-purple-500 text-white animate-pulse' 
                    : 'bg-white/10 hover:bg-white/20 text-gray-300'
                }`}
              >
                {isSpeaking ? <Volume2 size={22} /> : <Headphones size={22} />}
              </button>
            </div>
            <h2 className="text-2xl md:text-3xl font-medium leading-relaxed text-white">
              {currentQuestion}
            </h2>
          </div>
          
          {/* Recording Section */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 mb-6 border border-white/10">
            {/* Mic Button */}
            <div className="flex flex-col items-center mb-6">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isRecording 
                    ? 'bg-gradient-to-br from-red-500 to-rose-600' 
                    : 'bg-gradient-to-br from-purple-500 to-blue-600 hover:from-purple-400 hover:to-blue-500'
                } shadow-2xl`}
              >
                {isRecording && (
                  <>
                    <div className="absolute inset-0 rounded-full bg-red-500/50 animate-ping"></div>
                    <div className="absolute -inset-4 rounded-full border-4 border-red-500/30 animate-pulse"></div>
                  </>
                )}
                {isRecording ? <Square size={40} /> : <Mic size={40} />}
              </button>
              <p className="mt-4 text-gray-400 text-center">
                {isRecording ? (
                  <span className="flex items-center gap-2 text-red-400">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Kaydediliyor... Durdurmak için tıklayın
                  </span>
                ) : 'Mikrofona tıklayın ve cevabınızı söyleyin'}
              </p>
            </div>
            
            {/* Transcript */}
            <div className="bg-black/40 rounded-2xl p-5 min-h-[140px] border border-white/10">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                <MessageSquare size={14} />
                Cevabınız
              </div>
              <p className="text-gray-200 leading-relaxed text-lg">
                {transcript || (
                  <span className="text-gray-600 italic">Henüz bir şey söylemediniz...</span>
                )}
              </p>
            </div>
            
            {/* Save Recording Button */}
            {recordedAudioBlob && transcript && (
              <button
                onClick={saveRecordingToServer}
                className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <CheckCircle size={20} />
                Ses Kaydını Kaydet
              </button>
            )}
          </div>
          
          {/* Analyze Button */}
          {!showAnalysis && (
            <button
              onClick={analyzeAnswer}
              disabled={!transcript.trim() || isAnalyzing}
              className={`w-full py-5 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all duration-300 ${
                !transcript.trim() || isAnalyzing
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 shadow-xl shadow-emerald-500/30'
              }`}
            >
              {isAnalyzing ? (
                <>
                  <Loader className="animate-spin" size={24} />
                  AI Analiz Ediyor...
                </>
              ) : (
                <>
                  <Sparkles size={24} />
                  Cevabımı Değerlendir
                </>
              )}
            </button>
          )}
          
          {/* Analysis Result */}
          {showAnalysis && analysis && (
            <div className="space-y-5 animate-fadeIn">
              {/* Score Circle */}
              <div className={`bg-gradient-to-br ${getScoreGradient(analysis.score)}/20 rounded-3xl p-8 border border-white/10 text-center`}>
                <div className={`text-7xl font-black ${getScoreColor(analysis.score)}`}>
                  {analysis.score}
                </div>
                <div className="text-gray-400 mt-2 text-lg">Puan</div>
                <div className="mt-4 flex justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      size={24} 
                      className={i < Math.round(analysis.score / 20) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'} 
                    />
                  ))}
                </div>
              </div>
              
              {/* Feedback */}
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-gray-200 text-lg leading-relaxed">{analysis.feedback}</p>
              </div>
              
              {/* Strengths & Improvements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.strengths?.length > 0 && (
                  <div className="bg-emerald-500/10 rounded-2xl p-5 border border-emerald-500/20">
                    <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-3">
                      <CheckCircle size={20} />
                      Güçlü Yönler
                    </div>
                    <ul className="space-y-2">
                      {analysis.strengths.map((item, i) => (
                        <li key={i} className="text-gray-300 flex items-start gap-2">
                          <span className="text-emerald-400 mt-1">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {analysis.improvements?.length > 0 && (
                  <div className="bg-amber-500/10 rounded-2xl p-5 border border-amber-500/20">
                    <div className="flex items-center gap-2 text-amber-400 font-semibold mb-3">
                      <TrendingUp size={20} />
                      Geliştirilecekler
                    </div>
                    <ul className="space-y-2">
                      {analysis.improvements.map((item, i) => (
                        <li key={i} className="text-gray-300 flex items-start gap-2">
                          <span className="text-amber-400 mt-1">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Ideal Answer */}
              {analysis.ideal_answer && (
                <div className="bg-blue-500/10 rounded-2xl p-5 border border-blue-500/20">
                  <div className="flex items-center gap-2 text-blue-400 font-semibold mb-3">
                    <Star size={20} />
                    Örnek İdeal Cevap
                  </div>
                  <p className="text-gray-300 leading-relaxed">{analysis.ideal_answer}</p>
                </div>
              )}
              
              {/* Next Button */}
              <button
                onClick={nextQuestion}
                className="w-full py-5 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all shadow-xl"
              >
                {questionNumber < totalQuestions ? (
                  <>Sonraki Soru <ChevronRight size={24} /></>
                ) : (
                  <>Sonuçları Gör <Trophy size={24} /></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Results Screen
  if (stage === 'result' && results) {
    const avgScore = results.average_score;
    
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white">
        {/* Confetti Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
          {avgScore >= 70 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          )}
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-8 max-w-3xl">
          {/* Trophy */}
          <div className="text-center mb-10">
            <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br ${getScoreGradient(avgScore)} mb-6 shadow-2xl`}>
              <Trophy size={64} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">Mülakat Tamamlandı!</h1>
            <p className="text-gray-400 text-lg">İşte performans analiziniz</p>
          </div>
          
          {/* Main Score */}
          <div className={`bg-gradient-to-br ${getScoreGradient(avgScore)}/20 rounded-3xl p-10 mb-8 border border-white/20 text-center`}>
            <div className={`text-8xl font-black ${getScoreColor(avgScore)}`}>
              {avgScore}
            </div>
            <div className="text-gray-300 mt-2 text-xl">Ortalama Puan</div>
            <div className="text-gray-500 mt-1">{results.total_questions} sorudan</div>
            <div className="mt-6 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={32} 
                  className={i < Math.round(avgScore / 20) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'} 
                />
              ))}
            </div>
          </div>
          
          {/* Question Results */}
          <div className="bg-white/5 rounded-3xl p-6 mb-8 border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="text-purple-400" />
              Soru Detayları
            </h2>
            <div className="space-y-3">
              {results.answers?.map((answer, index) => (
                <div key={index} className="bg-black/30 rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm text-purple-400 mb-1">Soru {index + 1}</div>
                    <div className="text-gray-300 text-sm line-clamp-1">{answer.question}</div>
                  </div>
                  <div className={`text-3xl font-bold ${getScoreColor(answer.score)} ml-4`}>
                    {answer.score}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => {
                setStage('welcome');
                setSessionId(null);
                setResults(null);
                setTranscript('');
                setAnalysis(null);
                setShowAnalysis(false);
                setAllAnalyses([]);
              }}
              className="py-4 bg-white/10 hover:bg-white/20 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all"
            >
              <RefreshCw size={20} />
              Tekrar Dene
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all"
            >
              Ana Menü
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default AiVoiceInterview;
